import React, { useContext, useEffect, useState, useRef } from "react";
import StatesDropdown from "components/common/StatesDropdown";
import { MobileContext } from "components/common";
import { useApolloClient } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

const parser = require("parse-address");

const autoCompletedAddressFormats = {
  street_number: "short_name",
  route: "long_name",
  locality: "long_name",
  administrative_area_level_1: "short_name",
  country: "long_name",
  postal_code: "short_name"
};

const VALIDATE_ADDRESS_QUERY = gql`
  query ValidateAddress(
    $address: ValidateAddressInput
    $options: ValidateAddressInputOptions
  ) {
    validateAddress(address: $address, options: $options) {
      ok
      error
      address {
        street1
        street2
        state
        city
        zip
        zip4
      }
      invalid {
        primary_number
        secondary_number
        box_number
      }
      notes
    }
  }
`;

const NewAddressForm = ({
  address: defaultAddress,
  city: defaultCity,
  state: defaultState,
  zip: defaultZip,
  unitNumber: defaultUnitNumber,
  type,
  save = () => {},
  loading,
  setLoading = () => {},
  shouldValidate = false,
  resetSuccess,
  addressValid,
  SettingsInputContainer
}) => {
  const [error, setError] = useState(null);
  const [addressError, setAddressError] = useState(null);
  const [networkingError, setNetworkingError] = useState(null);
  const [autoCompletedAddress, setAutoCompletedAddress] = useState({});
  const [parsedAddress, setParsedAddress] = useState({});
  const [address, setAddress] = useState(defaultAddress || "");
  const [city, setCity] = useState(defaultCity || "");
  const [state, setState] = useState(defaultState || "");
  const [zip, setZip] = useState(defaultZip || "");
  const [unitNumber, setUnitNumber] = useState(defaultUnitNumber || "");
  const addressInput = useRef(null);
  let autocomplete = useRef(null);
  const { isMobile } = useContext(MobileContext);
  const client = useApolloClient();

  const initAutocomplete = () => {
    autocomplete = new window.google.maps.places.Autocomplete(
      document.getElementById(`autocomplete-${type}`),
      { types: ["geocode"] }
    );
    autocomplete.setFields(["address_component"]);
    autocomplete.addListener("place_changed", () => {
      console.log("Place changed");
      fill();
    });
  };

  // Update our parsed address in case we add unit information
  // Catches incorrect/double unit submission
  useEffect(() => {
    if (address.length) {
      parse(address);
    }
  }, [unitNumber]); //eslint-disable-line

  const build = () => {
    const payload = {};
    if (parsedAddress.number && parsedAddress.street) {
      console.log("We have parsed number and street");
      const parsedStreet = `${parsedAddress.number} ${parsedAddress.street}${
        parsedAddress.type ? ` ${parsedAddress.type}` : ""
      }`;

      // There is an edge case where the parser fails to grab what might be
      // a unit number. We will take the chance and pass the full address
      // to our validation API if it's longer than our parsed payload
      if (parsedStreet.length < address.length) {
        payload.address_street = address;
      } else {
        payload.address_street = parsedStreet;
      }
    } else if (
      autoCompletedAddress.street_number &&
      autoCompletedAddress.route
    ) {
      console.log("We have autoCompletedAddress street and route");
      payload.address_street = `${autoCompletedAddress.street_number} ${autoCompletedAddress.route}`;
    } else {
      console.log(
        "We have no autoCompletedAddress street and route nor parsed number and street"
      );
      payload.address_street = address;
    }

    // Google Places doesn't do unit numbers. Add manually.
    const parsedPayload = parse(payload.address_street, false);
    if (parsedPayload.sec_unit_num) {
      console.log("We already have unit information in the payload.");
    } else if (parsedAddress.sec_unit_type) {
      console.log(
        "We did not have unit information in the payload, but found data in parsedAddress. Adding unit information."
      );
      const parsedUnit = `${parsedAddress.sec_unit_type || "Unit"} ${
        parsedAddress.sec_unit_num
      }`;
      payload.address_street = `${payload.address_street} ${parsedUnit}`;
    } else if (unitNumber) {
      console.log(
        "Build: We have no parsed unit information in either the existing payload or the parsedAddress, but we do have unitNumber:",
        unitNumber
      );
      payload.address_street = `${payload.address_street} Unit ${unitNumber}`;
    }

    if (autoCompletedAddress.locality) {
      payload.address_city = autoCompletedAddress.locality;
    } else if (parsedAddress.city) {
      payload.address_city = parsedAddress.city;
    } else {
      payload.address_city = city;
    }

    if (autoCompletedAddress.administrative_area_level_1) {
      payload.address_subdivision =
        autoCompletedAddress.administrative_area_level_1;
    } else if (parsedAddress.state) {
      payload.address_subdivision = parsedAddress.state;
    } else {
      payload.address_subdivision = state;
    }

    payload.address_country_code = "US"; // always, for now

    if (autoCompletedAddress.postal_code) {
      payload.address_postal_code = autoCompletedAddress.postal_code;
    } else if (parsedAddress.zip) {
      payload.address_postal_code = parsedAddress.zip;
    } else {
      payload.address_postal_code = `${zip}`;
    }

    return payload;
  };

  const fill = () => {
    console.log("Filling");
    const place = autocomplete.getPlace();
    console.log("Filling with:", place);

    if (!place) {
      console.log("Couldn't get place from autocomplete!");
      return;
    }

    if (!place.address_components) {
      console.log("Address could not be parsed. Likely too short.");
      return;
    }

    const n = {
      street_number: "",
      route: "",
      locality: "",
      administrative_area_level_1: "",
      country: "",
      postal_code: ""
    };

    for (let i = 0; i < place.address_components.length; i++) {
      const addressType = place.address_components[i].types[0];
      if (autoCompletedAddressFormats[addressType]) {
        const val =
          place.address_components[i][autoCompletedAddressFormats[addressType]];
        n[addressType] = val || "";
      }
    }

    const [a, c, s, z] = [
      n.street_number && n.route ? `${`${n.street_number} ${n.route}`}` : "",
      n.locality || "",
      n.administrative_area_level_1 || "",
      n.postal_code || ""
    ];

    console.log("About to set autoCompletedAddress");
    setAutoCompletedAddress(n);
    // console.log("About to set formattedAddress");
    // setFormattedAddress(a);
    console.log("About to set address");
    setAddress(a);
    console.log("About to set city");
    setCity(c);
    console.log("About to set state");
    setState(s);
    console.log("About to set zip");
    setZip(z);

    console.log("About to Parse");
    parse(`${a} ${c} ${s} ${z}`);
  };

  const parse = (string, tryUnit = true) => {
    // This method will extract things like unit numbers from the
    // autocomplete field, in case the user enters their address
    // there instead of more appropriate fields
    // It also gives us a more granular breakdown of the address components,
    // which helps us format payloads sent to address validation APIs
    const parsed = parser.parseLocation(
      `${string}${
        tryUnit ? (unitNumber.length > 0 ? ` ${unitNumber}` : "") : ""
      }`
    );
    if (!parsed) {
      console.log("Couldn't parse");
      return null;
    }
    console.log("Setting parsed address -", parsed);
    setParsedAddress({ ...parsed });
    return parsed;
  };

  const areFieldsValid = () => {
    if (!address || !city || !state || !zip) {
      setError("A valid street, city, state, and zip are required");
      return false;
    }
    setError(null);
    setAddressError(null);
    return true;
  };

  const validate = async () => {
    setError(null);
    setNetworkingError(null);
    setAddressError(null);
    if (!isMobile) resetSuccess();

    if (!areFieldsValid()) {
      return false;
    }

    const {
      city: pCity,
      number: pNumber,
      sec_unit_num: pUnitNum,
      sec_unit_type: pUnitType,
      state: pState,
      street: pStreet,
      type: pType,
      zip: pZip
    } = parsedAddress;
    console.log("Google address is:", `${address} ${city} ${state} ${zip}`);
    console.log(
      "Parsed address is:",
      `${pNumber} ${pStreet} ${pType}, ${pUnitType} ${pUnitNum}, ${pCity}, ${pState}, ${pZip}`
    );
    // const parsedUnit = ` ${pUnitType || "Unit"} ${pUnitNum}`;

    console.log("build address:", build());

    const {
      address_street,
      address_city,
      address_subdivision,
      address_country_code,
      address_postal_code
    } = build();

    setLoading(true);
    client
      .query({
        query: VALIDATE_ADDRESS_QUERY,
        variables: {
          address: {
            street1: address_street,
            city: address_city,
            state: address_subdivision,
            zip: address_postal_code,
            country: address_country_code
          },
          options: {
            notes: true
          }
        },
        fetchPolicy: "network-only"
      })
      .then(x => {
        const {
          data: { validateAddress: v }
        } = x;
        setLoading(false);
        console.log("We have shit:", v);
        const { ok, error: vError, address: vAddress, invalid } = v;
        console.log(
          "ok",
          ok,
          "error",
          error,
          "address",
          vAddress,
          "invalid",
          invalid
        );

        console.log(
          "After resp, trying to parse",
          (vAddress || {}).street1 || ""
        );
        const parsed = parse((vAddress || {}).street1 || "", false);
        console.log("Parsed response address:", parsed);
        const { sec_unit_type, sec_unit_num } = parsed || {};
        if (sec_unit_type && sec_unit_num) {
          console.log(
            "Found",
            sec_unit_type,
            sec_unit_num,
            "in response, clearing local unitNumber"
          );
          setUnitNumber("");
        }
        if (ok) {
          // Set our inputs to match our validated data
          const {
            street1: vStreet,
            state: vState,
            city: vCity,
            zip: vZip
          } = vAddress;
          setAddress(vStreet);
          setState(vState);
          setCity(vCity);
          setZip(vZip);
          return save({ address: vAddress, type });
        }
        addressValid(false);
        if (invalid.primary_number) {
          setError("Primary number missing or invalid");
        }
        if (invalid.secondary_number) {
          setError("Secondary number missing or invalid");
        }
        if (vError) {
          setError("Something went wrong. Please try again later.");
        }
      })
      .catch(errors => {
        console.log("Got errors:", errors);
        setLoading(false);
        setError("Invalid address. Please check and try again.");
      });
  };

  useEffect(() => {
    initAutocomplete();
  }, []); //eslint-disable-line

  useEffect(() => {
    if (defaultAddress && defaultCity && defaultState && defaultZip) {
      // setFormattedAddress(defaultAddress);
      setAddress(defaultAddress);
    }
  }, []); //eslint-disable-line

  useEffect(() => {
    if (shouldValidate) {
      validate();
    }
  }, [shouldValidate]); //eslint-disable-line

  console.log("Rendering", type);
  return (
    <div style={{ maxWidth: 400 }}>
      {addressError && <div className="errorText pt-3">{addressError}</div>}
      <SettingsInputContainer className="mb-2">
        <input
          id={`autocomplete-${type}`}
          value={address}
          placeholder="Address"
          type="text"
          className={`w-full outline-none ${addressError && "redBorder"} ${
            loading ? "text-gray-500" : ""
          }`}
          onChange={({ target: { value: v } }) => {
            if (!v) {
              setAutoCompletedAddress({});
            }
            parse(v);
            setAddress(v);
          }}
          ref={addressInput}
          disabled={loading}
        />
      </SettingsInputContainer>
      <SettingsInputContainer className="mb-2">
        <input
          type="text"
          value={unitNumber}
          placeholder="Apt/Suite/Unit # (if applicable)"
          className={`w-full outline-none ${addressError && "redBorder"} ${
            loading ? "text-gray-500" : ""
          }`}
          onChange={({ target: { value: v } }) => {
            setUnitNumber(v);
          }}
          disabled={loading}
        />
      </SettingsInputContainer>
      <SettingsInputContainer className="mb-2">
        <input
          type="text"
          value={city}
          placeholder="City"
          className={`w-full outline-none ${addressError && "redBorder"} ${
            loading ? "text-gray-500" : ""
          }`}
          onChange={({ target: { value: v } }) => {
            setCity(v);
          }}
          disabled={loading}
        />
      </SettingsInputContainer>
      <div className={`flex ${isMobile ? "flex-col" : ""}`}>
        <div className="flex-1">
          <StatesDropdown
            didSelect={v => {
              setState(v.value);
            }}
            disabled={loading}
            defaultValue={state}
            SettingsInputContainer={SettingsInputContainer}
          />
        </div>
        <div className={`flex-1 ${isMobile ? "mt-2" : "pl-4"}`}>
          <SettingsInputContainer>
            <input
              type="text"
              value={zip}
              placeholder="Zip Code"
              className={`w-full outline-none ${addressError && "redBorder"} ${
                loading ? "text-gray-500" : ""
              }`}
              onChange={({ target: { value: v } }) => {
                setZip(v);
              }}
              disabled={loading}
            />
          </SettingsInputContainer>
        </div>
      </div>
      <div>
        {error && (
          <div
            className="text-red-500"
            style={{ position: "relative", top: 10 }}
          >
            {error}
          </div>
        )}
        {networkingError && (
          <div
            className="text-red-500"
            style={{ position: "relative", top: 10 }}
          >
            Error saving your data
          </div>
        )}
      </div>
    </div>
  );
};

export default NewAddressForm;
